/**
 * Coup de pouce — Attendus 1103 « connaitre les puissances simples : 2², 2³,
 * 3², 3³, 10², 10³… » (Puissances, l. 678) et 1104 « connaitre 10² et 10³ »
 * (l. 680, wrapper : les clés dix_* de gen_1103). UNE page STATIQUE partagée
 * par les six clés des deux ids.
 *
 * L'image que l'élève a déjà : le cube en couches de 1059 (5e, « m³, c'est
 * m × m × m ») et la grille de 1101 (13 rangées de 13). Un cube de 6 : la
 * face avant quadrillée en bleu, 6 × 6 = 36, une couche ; les 6 couches en
 * orange sur le côté. Le résultat en gros « 6³ = 6 × 6 × 6 = 216 » ; deux
 * lignes en langue d'élève : « 6² = 6 × 6 = 36 : une couche. » et
 * « 6 couches de 36 : 6 × 6 × 6 = 216. » ; puis le piège, nommé en une
 * ligne : « Le petit 3 : on multiplie 6 trois fois, pas 6 × 3. »
 *
 * EXEMPLE HORS PLAGE : base 6 (SIMPLES = bases 2, 3, 4, 5, 10 ; RARE = 1).
 * Rien à sacrifier. Largeur bornée à 340 px, tailles de 1048.
 */

const ORANGE = '#EA580C';
const ORANGE_CLAIR = '#FED7AA';
const BLEU = '#2563EB';
const BLEU_CLAIR = '#DBEAFE';
const ENCRE = '#1E293B';

const N = 6;
const C = 14;
const F = N * C;
const X2 = 138;
const Y2 = 96;
const DX = 40;
const DY = -32;
const dx = DX / N;
const dy = DY / N;

/** La face avant quadrillée en 6 × 6 : une couche de 36. */
const FACE_AVANT = [
    ...Array.from({ length: N + 1 }, (_, k) => `<line x1="${X2 + k * C}" y1="${Y2}" x2="${X2 + k * C}" y2="${Y2 + F}" stroke="${BLEU}" stroke-width="0.7"/>`),
    ...Array.from({ length: N + 1 }, (_, k) => `<line x1="${X2}" y1="${Y2 + k * C}" x2="${X2 + F}" y2="${Y2 + k * C}" stroke="${BLEU}" stroke-width="0.7"/>`),
].join('\n  ');
/** Les 6 couches, en profondeur : sur la face de droite et sur le dessus. */
const COUCHES = [
    ...Array.from({ length: N + 1 }, (_, k) => `<line x1="${X2 + F + k * dx}" y1="${Y2 + k * dy}" x2="${X2 + F + k * dx}" y2="${Y2 + F + k * dy}" stroke="${ORANGE}" stroke-width="0.9"/>`),
    ...Array.from({ length: N + 1 }, (_, k) => `<line x1="${X2 + k * dx}" y1="${Y2 + k * dy}" x2="${X2 + F + k * dx}" y2="${Y2 + k * dy}" stroke="${ORANGE}" stroke-width="0.9"/>`),
].join('\n  ');

const SVG_CUBE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 304" width="100%" style="max-width:340px;height:auto;display:block" role="img"
     aria-label="6 au cube, c'est combien ? Un cube de 6 sur 6 sur 6. La face avant est quadrillée en bleu, 6 cases sur 6 : une couche de 36. Sur le côté, 6 couches orange. 6 au cube égale 6 fois 6 fois 6 égale 216. 6 au carré égale 6 fois 6 égale 36 : une couche. 6 couches de 36 : 6 fois 6 fois 6 égale 216. Le petit 3 : on multiplie 6 trois fois, pas 6 fois 3.">
  <text x="200" y="30" font-size="16" font-weight="bold" text-anchor="middle" fill="${ENCRE}">6³, c'est combien ?</text>

  <!-- le cube : face avant bleue (une couche), côté et dessus en couches orange -->
  <polygon points="${X2 + F},${Y2} ${X2 + F + DX},${Y2 + DY} ${X2 + F + DX},${Y2 + DY + F} ${X2 + F},${Y2 + F}" fill="${ORANGE_CLAIR}" stroke="${ENCRE}" stroke-width="1.6"/>
  <polygon points="${X2},${Y2} ${X2 + DX},${Y2 + DY} ${X2 + F + DX},${Y2 + DY} ${X2 + F},${Y2}" fill="#FFFFFF" stroke="${ENCRE}" stroke-width="1.6"/>
  ${COUCHES}
  <rect x="${X2}" y="${Y2}" width="${F}" height="${F}" fill="${BLEU_CLAIR}" stroke="${ENCRE}" stroke-width="1.6"/>
  ${FACE_AVANT}
  <text x="${X2 + F / 2}" y="${Y2 + F + 18}" font-size="13" font-weight="bold" text-anchor="middle" fill="${BLEU}">6</text>
  <text x="${X2 - 10}" y="${Y2 + F / 2 + 5}" font-size="13" font-weight="bold" text-anchor="end" fill="${BLEU}">6</text>
  <text x="${X2 + F + DX + 10}" y="${Y2 + DY + F / 2 + 5}" font-size="13" font-weight="bold" fill="${ORANGE}">6</text>
  <text x="${X2 + F / 2 - 4}" y="${Y2 + F / 2 + 6}" font-size="15" font-weight="bold" text-anchor="middle" fill="${BLEU}">36</text>

  <!-- le résultat -->
  <text x="200" y="228" font-size="20" font-weight="bold" text-anchor="middle" fill="${ENCRE}">6³ = 6 × 6 × 6 = <tspan fill="${ORANGE}">216</tspan></text>

  <!-- le geste, puis le piège -->
  <text x="200" y="256" font-size="12" text-anchor="middle" fill="${ENCRE}">6² = 6 × 6 = <tspan font-weight="bold" fill="${BLEU}">36</tspan> : une couche.</text>
  <text x="200" y="276" font-size="12" text-anchor="middle" fill="${ENCRE}"><tspan font-weight="bold" fill="${ORANGE}">6 couches</tspan> de 36 : 6 × 6 × 6 = <tspan font-weight="bold" fill="${ORANGE}">216</tspan>.</text>
  <text x="200" y="296" font-size="12" text-anchor="middle" fill="${ENCRE}">Le petit 3 : on multiplie 6 trois fois, pas 6 × 3.</text>
</svg>`.trim();

const AIDE = { pages: [{ titre: '6 couches de 6 × 6', svg: SVG_CUBE }] };

const CP_1103 = {
    flash_texte_trou_saisie: AIDE,
    valeur_qcm_unique: AIDE,
    sens_tableau_vf: AIDE,
    dix_flash_texte_trou_saisie: AIDE,
    dix_valeur_qcm_unique: AIDE,
    dix_sens_tableau_vf: AIDE,
};

export default CP_1103;
